
import React, { useState, useEffect } from 'react';
import { useUpgradeGate } from './useUpgradeGate';
import { apiService } from '../services/apiService';
import { SavedItem, SavedCardType } from '../types';
import { SavedItemCard } from './SavedItemCard';
import TechCard from './TechCard';
import ResearcherCard from './ResearcherCard';
import { ContactUniversityModal } from './modals/ContactUniversityModal';
import { PreviewMessageModal } from './modals/PreviewMessageModal';
import { FloatingToast } from './InlineAlert';
import { friendlyAuthError } from '../utils/authErrors';

const initialSavedItems: SavedItem[] = [
  { id: 'r1', type: SavedCardType.Researcher, relevance: 55, title: 'Robert Masson', location: 'University of Arizona, United States', detail: '5 Research cards', verified: true, showStatusIcon: true },
  { id: 't1', type: SavedCardType.Tech, relevance: 55, title: 'Martin Irrigation Head: Efficient Solid Set Flood Irrigation', location: 'University of Arizona, United States', detail: 'TRL 5', verified: true, showStatusIcon: true },
  { id: 't2', type: SavedCardType.Tech, relevance: 55, title: 'Martin Irrigation Head: Efficient Solid Set Flood Irrigation', location: 'University of Arizona, United States', detail: 'TRL 5', verified: false, showStatusIcon: false },
  { id: 't3', type: SavedCardType.Tech, relevance: 55, title: 'Martin Irrigation Head: Efficient Solid Set Flood Irrigation', location: 'University of Arizona, United States', detail: 'TRL 5', verified: true, showStatusIcon: true },
  { id: 't4', type: SavedCardType.Tech, relevance: 55, title: 'Martin Irrigation Head: Efficient Solid Set Flood Irrigation', location: 'University of Arizona, United States', detail: 'TRL 5', verified: false, showStatusIcon: false },
  { id: 'r2', type: SavedCardType.Researcher, relevance: 55, title: 'Jane Smith', location: 'Stanford University, United States', detail: '3 Research cards', verified: false, showStatusIcon: true },
  { id: 't5', type: SavedCardType.Tech, relevance: 55, title: 'New Solar Panel Technology', location: 'MIT, United States', detail: 'TRL 7', verified: true, showStatusIcon: false },
  { id: 't6', type: SavedCardType.Tech, relevance: 55, title: 'AI-Powered Crop Monitoring', location: 'UC Berkeley, United States', detail: 'TRL 4', verified: true, showStatusIcon: true },
  { id: 't7', type: SavedCardType.Tech, relevance: 55, title: 'Water Purification System', location: 'Caltech, United States', detail: 'TRL 6', verified: false, showStatusIcon: false },
  { id: 't8', type: SavedCardType.Tech, relevance: 55, title: 'Advanced Drone Irrigation', location: 'Cornell University, United States', detail: 'TRL 5', verified: true, showStatusIcon: true },
];

interface SavedPageProps {
    profileData?: {
        firstName: string;
        lastName: string;
        summary: string;
    };
    rdProfileData?: {
        rdSummary: string;
    };
    /** Opens the concierge booking form; owned by App, which holds modal state. */
    onTalkToConcierge?: () => void;
    /** Opens a saved technology in App's card modal — the same card For You
     *  and search open (owner, 2026-09-29) — instead of this page's legacy one. */
    onOpenTech?: (techId: string) => void;
}

export const SavedPage = ({ profileData, rdProfileData, onTalkToConcierge, onOpenTech }: SavedPageProps = {}) => {
    const upgrade = useUpgradeGate();
    const [savedItems, setSavedItems] = useState<SavedItem[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [modalOpen, setModalOpen] = useState<{type: 'tech' | 'researcher', data: any} | null>(null);
    const [contactModalOpen, setContactModalOpen] = useState(false);
    const [previewModalOpen, setPreviewModalOpen] = useState(false);
    const [currentContactData, setCurrentContactData] = useState<any>(null);
    const [previewData, setPreviewData] = useState<{message: string, summaryType: 'personal' | 'rd', techManagerName: string} | null>(null);
    const [successMessage, setSuccessMessage] = useState<string>('');
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [savedStates, setSavedStates] = useState<Map<string, {bookmarked: boolean, liked: boolean | null}>>(new Map());


    // Reloaded when a bookmark changes in App's card modal (opened from here):
    // an item un-bookmarked there must leave this list.
    const [reloadKey, setReloadKey] = useState(0);
    useEffect(() => {
        const on = () => setReloadKey(k => k + 1);
        window.addEventListener('utech:saved-changed', on);
        return () => window.removeEventListener('utech:saved-changed', on);
    }, []);

    useEffect(() => {
        const loadSavedCards = async () => {
            try {
                const userId = apiService.getStoredUserId();
                if (!userId) {
                    setError('User session not found');
                    setIsLoading(false);
                    return;
                }

                const response = await apiService.getSavedCards(userId, 'all');
                if (response.success && response.data) {
                    // Backend returns {items: [...], counts: {...}}
                    const apiData = response.data;
                    const allItems = apiData.items || [];
                    
                    // Filter to only show bookmarked items (bookmarked: true)
                    const bookmarkedItems = allItems.filter((item: any) => item.bookmarked === true);
                    
                    console.log(`📚 Saved items filter: ${allItems.length} total items, ${bookmarkedItems.length} bookmarked items`);
                    
                    // Map API response to SavedItem format with proper data
                    const savedCards: SavedItem[] = await Promise.all(bookmarkedItems.map(async (item: any) => {
                        const createdDate = new Date(item.created_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                        });
                        
                        let title = item.asset_id;
                        let location = 'Unknown Location';
                        let detail = item.asset_type === 'technology' ? 'Technology' : 'Researcher';
                        
                        try {
                            // Fetch asset details to get proper title, location, and TRL
                            if (item.asset_type === 'technology') {
                                const techResponse = await apiService.getTechnologyDetails(item.asset_id);
                                if (techResponse.success && techResponse.data) {
                                    const techData = techResponse.data;
                                    title = techData.utech_title || techData.title || item.asset_id;
                                    location = [techData.university_name, techData.university_country]
                                        .filter(Boolean).join(', ') || 'Unknown Location';
                                    // Extract TRL value - handle "Not Disclosed" case
                                    let trlDisplay = 'TRL Not Disclosed';
                                    if (techData.trl) {
                                        if (typeof techData.trl === 'string') {
                                            // If it contains "not disclosed", keep it as is
                                            if (techData.trl.toLowerCase().includes('not disclosed')) {
                                                trlDisplay = 'TRL Not Disclosed';
                                            } else {
                                                // It's a formatted string like "TRL 5"
                                                trlDisplay = techData.trl;
                                            }
                                        } else if (typeof techData.trl === 'object' && techData.trl.calculated_trl) {
                                            const calcTrl = String(techData.trl.calculated_trl).toLowerCase();
                                            if (calcTrl.includes('not disclosed')) {
                                                trlDisplay = 'TRL Not Disclosed';
                                            } else {
                                                const match = calcTrl.match(/(\d+)/);
                                                trlDisplay = match ? `TRL ${match[1]}` : 'TRL Not Disclosed';
                                            }
                                        }
                                    }
                                    detail = trlDisplay;
                                }
                            } else {
                                const researcherResponse = await apiService.getResearcherDetails(item.asset_id);
                                if (researcherResponse.success && researcherResponse.data) {
                                    const researcherData = researcherResponse.data;
                                    title = researcherData.name || researcherData.researcher_name || item.asset_id;
                                    location = researcherData.university_name && researcherData.university_country
                                        ? `${researcherData.university_name}, ${researcherData.university_country}`
                                        : 'Unknown Location';
                                    const techCount = researcherData.technologies?.length || 0;
                                    detail = `${techCount} research area${techCount !== 1 ? 's' : ''}`;
                                }
                            }
                        } catch (error) {
                            console.error(`Failed to fetch details for ${item.asset_type} ${item.asset_id}:`, error);
                        }
                        
                        return {
                            id: item.saved_id || item.id,
                            type: item.asset_type === 'technology' ? SavedCardType.Tech : SavedCardType.Researcher,
                            relevance: `Saved on ${createdDate}`, // Replace relevance with saved date
                            title: title,
                            location: location,
                            detail: detail,
                            verified: false,
                            showStatusIcon: false, // Remove status icon as requested
                            // Store raw data for modal
                            rawData: item
                        };
                    }));
                    setSavedItems(savedCards);
                } else {
                    setError(friendlyAuthError(response.error, 'Failed to load saved cards.'));
                }
            } catch (error) {
                console.error('Error loading saved cards:', error);
                setError('Network error. Please try again.');
            } finally {
                setIsLoading(false);
            }
        };

        loadSavedCards();
    }, [reloadKey]);

    // Load saved states for like/dislike/bookmark when modal opens
    const loadSavedStates = async (assetType: string, assetId: string) => {
        try {
            const userId = apiService.getStoredUserId();
            if (!userId) return {bookmarked: true, liked: null}; // Default for saved items
            
            // For saved items, we assume they're bookmarked
            // Load like status from the liked endpoint
            const response = await apiService.getLikedCards(userId, assetType);
            if (response.success && response.data?.items) {
                const likedItem = response.data.items.find((item: any) => item.asset_id === assetId);
                const liked = likedItem ? likedItem.liked : null;
                
                const state = {bookmarked: true, liked};
                setSavedStates(prev => new Map(prev).set(assetId, state));
                return state;
            }
            
            return {bookmarked: true, liked: null};
        } catch (error) {
            console.error('Error loading saved states:', error);
            return {bookmarked: true, liked: null};
        }
    };

    const handleBookmarkToggle = (id: string) => {
        setSavedItems(prev => prev.filter(item => item.id !== id));
    };



    const handleReadMore = async (type: SavedCardType, id: string) => {
        try {
            // Find the saved item to get its asset_id and raw data
            const savedItem = savedItems.find(item => item.id === id);
            if (!savedItem || !savedItem.rawData) return;

            const assetId = savedItem.rawData.asset_id;
            const assetType = savedItem.rawData.asset_type;
            if (type === SavedCardType.Tech && onOpenTech && assetId) {
                onOpenTech(String(assetId));
                return;
            }

            // Load the current saved states
            await loadSavedStates(assetType, assetId);

            // Fetch actual asset details from backend
            if (type === SavedCardType.Tech) {
                const response = await apiService.getTechnologyDetails(assetId);
                if (response.success && response.data) {
                    const techData = response.data;
                    
                    // Extract TRL value - handle "Not Disclosed" case
                    let trlDisplay = 'TRL Not Disclosed';
                    if (techData.trl) {
                        if (typeof techData.trl === 'string') {
                            // If it contains "not disclosed", keep it as is
                            if (techData.trl.toLowerCase().includes('not disclosed')) {
                                trlDisplay = 'TRL Not Disclosed';
                            } else {
                                // It's a formatted string like "TRL 5"
                                trlDisplay = techData.trl;
                            }
                        } else if (typeof techData.trl === 'object' && techData.trl.calculated_trl) {
                            const calcTrl = String(techData.trl.calculated_trl).toLowerCase();
                            if (calcTrl.includes('not disclosed')) {
                                trlDisplay = 'TRL Not Disclosed';
                            } else {
                                const match = calcTrl.match(/(\d+)/);
                                trlDisplay = match ? `TRL ${match[1]}` : 'TRL Not Disclosed';
                            }
                        }
                    }
                    
                    const researchers = techData.researchers || [{ name: 'Researcher', url: '' }];
                    
                    // Get saved date for relevance score
                    const savedDate = new Date(savedItem.rawData.created_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                    });
                    
                    // Extract IP and publication summaries from ip_information
                    let ipSummary = 'No IP summary available';
                    let publicationsSummary = 'No publications summary available';
                    
                    if (techData.ip_information) {
                        try {
                            const ipInfo = typeof techData.ip_information === 'string' 
                                ? JSON.parse(techData.ip_information)
                                : techData.ip_information;
                            ipSummary = ipInfo.ip_summary || techData.ip_summary || ipSummary;
                            publicationsSummary = ipInfo.publications_summary || techData.publications_summary || publicationsSummary;
                        } catch (e) {
                            console.warn('Could not parse ip_information:', e);
                            // Fallback to direct fields if parsing fails
                            ipSummary = techData.ip_summary || ipSummary;
                            publicationsSummary = techData.publications_summary || publicationsSummary;
                        }
                    }

                    const modalData = {
                        id: assetId,
                        utech_technology_id: assetId, // Include for API calls
                        relevanceScore: 'Technology Card', // Show 'Technology Card' instead of date
                        title: techData.utech_title || techData.title || assetId,
                        institution: techData.university_name || '',
                        location: techData.university_country || '',
                        trl: trlDisplay,
                        trlReasoning: trlDisplay.toLowerCase().includes('not disclosed')
                            ? 'TRL not disclosed. Please contact the university for more details.'
                            : techData.trl_reasoning,
                        researchers: researchers,
                        summary: techData.utech_summary || techData.summary || 'Technology summary not available',
                        ipSummary: ipSummary,
                        publicationsSummary: publicationsSummary,
                        activeTab: "Summary" as const,
                        onContact: () => handleContactUniversity({
                            techTitle: techData.utech_title || techData.title || assetId,
                            techId: techData.utech_technology_id || assetId, // Always use utech_technology_id for data fetching
                            // Where it was opened + the university, for the
                            // contacts request and the Talk to Concierge lead.
                            surface: 'saved',
                            university: techData.university_name || '',
                            referenceNumber: techData.reference_number || '', // Separate reference number for display
                            researcherName: researchers[0]?.name || 'Researcher',
                            userName: profileData ? `${profileData.firstName} ${profileData.lastName}` : '',
                            personalSummary: profileData?.summary || '',
                            rdSummary: rdProfileData?.rdSummary || ''
                        }),
                        onClose: () => setModalOpen(null)
                    };
                    setModalOpen({type: 'tech', data: modalData});
                } else {
                    console.error('Failed to fetch technology details:', response.error);
                }
            } else {
                const response = await apiService.getResearcherDetails(assetId);
                if (response.success && response.data) {
                    const researcherData = response.data;
                    const techs = researcherData.technologies || [];
                    
                    // Get saved date for relevance score
                    const savedDate = new Date(savedItem.rawData.created_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                    });
                    
                    const modalData = {
                        id: assetId,
                        utech_researcher_id: researcherData.utech_researcher_id || assetId, // Include for API calls
                        relevanceScore: `Saved on ${savedDate}`, // Use saved date instead of relevance score
                        title: researcherData.name || researcherData.researcher_name || assetId,
                        institution: researcherData.university_name || 'Unknown University',
                        location: researcherData.university_country || 'Unknown Location',
                        techs: (researcherData.technologies || researcherData.techs || []).map((tech: any, index: number) => ({
                            id: tech.utech_technology_id || tech.id || index.toString(),
                            title: tech.title || tech.utech_title || 'Related Technology'
                        }))
                    };
                    setModalOpen({type: 'researcher', data: modalData});
                } else {
                    console.error('Failed to fetch researcher details:', response.error);
                }
            }
        } catch (error) {
            console.error('Failed to load asset details:', error);
        }
    };

    const handleContactUniversity = (techData: any) => {
        setCurrentContactData(techData);
        setContactModalOpen(true);
    };

    const handlePreviewMessage = (message: string, summaryType: 'personal' | 'rd',
                                  techManagerName: string) => {
        setPreviewData({message, summaryType, techManagerName});
        setContactModalOpen(false);
        setPreviewModalOpen(true);
    };

    const handleSendEmail = async (emailData: {
        subject: string;
        content: string;
        techId: string;
        researcherName: string;
    }) => {
        try {
            const userId = apiService.getStoredUserId();
            if (!userId) {
                console.error('User not logged in');
                return;
            }

            // Generate a unique conversation ID
            const conversationId = `${emailData.techId}-${Date.now()}-${userId}`;
            
            // Format content as HTML
            const htmlContent = `
                <html>
                <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
                    <div style="max-width: 600px; margin: 0 auto; padding: 20px;">
                        <h2 style="color: #2c3e50; margin-bottom: 20px;">Technology Partnership Opportunity</h2>
                        
                        <p style="margin-bottom: 15px;">Dear ${emailData.researcherName},</p>
                        
                        <div style="background-color: #f8f9fa; padding: 20px; border-radius: 8px; margin: 20px 0;">
                            ${emailData.content.replace(/\n\n/g, '</p><p style="margin-bottom: 15px;">').replace(/\n/g, '<br>')}
                        </div>
                        
                        <p style="margin-top: 20px; font-size: 14px; color: #666;">
                            This email was sent through <a href="https://universitytech.io" style="color: #007bff; text-decoration: none;">UniversityTech.io</a> 
                            to facilitate technology transfer and collaboration opportunities.
                        </p>
                        
                        <hr style="border: none; border-top: 1px solid #eee; margin: 20px 0;">
                        
                        <p style="font-size: 12px; color: #999; text-align: center;">
                            This message was sent via UniversityTech.io's secure email system.
                        </p>
                    </div>
                </body>
                </html>
            `;
            
            // Fetch tech data to get tech manager info
            let techManagerEmails: string[] = [];
            let techManagerNames: string[] = [];
            let commonEmail: string | undefined = undefined;
            let ccEmail: string | undefined = undefined;

            // Set when the account is over its university-contact limit: the
            // contacts endpoint withholds the addresses, so there is no one to send
            // to (and routing it to ops would be wrong too).
            let contactBlockReason: string | null = null;

            try {
                // The metered contacts endpoint: the same four fields the details
                // endpoint used to carry, under `contacts`.
                const contactsResponse = await apiService.getTechnologyContacts(emailData.techId,
                    { surface: 'send_email', purpose: 'send' });
                if (contactsResponse.success && contactsResponse.data && !contactsResponse.data.allowed) {
                    contactBlockReason = contactsResponse.data.reason || 'limit_reached';
                }
                const techContacts: any = contactsResponse.success && contactsResponse.data?.allowed
                    ? contactsResponse.data.contacts : null;
                if (techContacts) {
                    commonEmail = techContacts.common_email || undefined;

                    // Curated tech-transfer office (technologies.tto_office_details), loaded
                    // from the reviewed Global Universities sheet. NULL means not curated yet.
                    // The details endpoint may hand JSON columns back as strings, so parse it
                    // the same way tech_manager_default is treated elsewhere: object, JSON
                    // string, or nothing. Its emails are always an array of clean addresses.
                    const rawOffice = techContacts.tto_office_details;
                    let ttoOffice: any = null;
                    try {
                        ttoOffice = typeof rawOffice === 'object' && rawOffice !== null
                            ? rawOffice
                            : (typeof rawOffice === 'string' && rawOffice.trim() ? JSON.parse(rawOffice) : null);
                    } catch (e) {
                        ttoOffice = null;
                    }
                    const ttoOfficeEmails: string[] = Array.isArray(ttoOffice?.emails)
                        ? ttoOffice.emails.filter((e: any) => typeof e === 'string' && e.trim())
                        : [];

                    // CC: the curated office when we have one (every address; the backend
                    // splits cc_email on ','/';' and drops whichever one becomes TO), else
                    // the legacy common_email string as before.
                    ccEmail = ttoOfficeEmails.length > 0 ? ttoOfficeEmails.join(', ') : commonEmail;

                    // 1. Try tech_manager_details (list or dict)
                    const techManagerDetails = techContacts.tech_manager_details;
                    if (techManagerDetails) {
                        const detailsList = Array.isArray(techManagerDetails) ? techManagerDetails : [techManagerDetails];
                        detailsList.forEach((manager: any) => {
                            if (manager?.email) {
                                techManagerEmails.push(manager.email);
                                if (manager.name) techManagerNames.push(manager.name);
                            }
                        });
                    }
                    
                    // 2. Fallback: curated office first, then tech_manager_default.
                    // The curated office is the reviewed contact; tech_manager_default is
                    // scraped, and for 7 universities its "name" is the university's own
                    // name rather than an office (WARF's is a person), so it only comes
                    // into play when nothing curated exists.
                    if (techManagerEmails.length === 0 && ttoOfficeEmails.length > 0) {
                        techManagerEmails.push(ttoOfficeEmails[0]);
                        if (ttoOffice?.name) techManagerNames.push(ttoOffice.name);
                    }
                    if (techManagerEmails.length === 0) {
                        const techManagerDefault = techContacts.tech_manager_default;
                        if (techManagerDefault?.email) {
                            techManagerEmails.push(techManagerDefault.email);
                            if (techManagerDefault.name) techManagerNames.push(techManagerDefault.name);
                        }
                    }
                }
            } catch (error) {
                console.warn('Could not fetch tech manager info:', error);
            }

            if (contactBlockReason) {
                setErrorMessage(contactBlockReason === 'upgrade_required'
                    ? "University contacts are part of our trial and paid plans, so this message wasn't sent."
                    : "You've reached your limit for university contacts, so this message wasn't sent. "
                        + 'You can request more from the Contact University window.');
                setTimeout(() => setErrorMessage(''), 8000);
                return;
            }
            
            // 3. Final fallback — route to ops, never to a personal address.
            // See Backend/DOCS/BUG_2026-05-13_misrouted_tto_email.md.
            //
            // Raw field contents: each may hold several ';'/','-separated addresses.
            // The backend picks the final TO/CC (api.send_tracked_email). A CC list
            // (curated office emails, else common_email) must be passed here too —
            // without it the backend has nothing to promote when the tech-manager
            // field isn't a single valid address, and this page would route
            // differently from the other two send paths.
            const universityEmail = techManagerEmails.length > 0
                ? techManagerEmails[0]
                : (commonEmail || 'info@universitytech.io');
            
            const allTechManagerEmails = techManagerEmails.length > 0 
                ? techManagerEmails.join(', ') 
                : 'Not available in data';
            const techManagerName = techManagerNames.length > 0 
                ? techManagerNames.join(', ') 
                : 'Unknown';
            
            console.log('📧 Sending email to university:', universityEmail);
            console.log('   Tech ID:', emailData.techId);
            console.log('   Subject:', emailData.subject);
            console.log('   Researcher Name:', emailData.researcherName);
            console.log('   User ID:', userId);
            console.log('   🏛️ Actual Tech Manager Name(s):', techManagerName);
            console.log('   📧 All Tech Manager Email(s):', allTechManagerEmails);
            console.log('   📧 Primary Email (TO):', universityEmail);
            console.log('   📧 CC:', ccEmail || 'Not available');
            
            const emailPayload = {
                to_email: universityEmail, // universityEmail variable - change above to switch recipients
                subject: emailData.subject,
                content: htmlContent,
                conversation_id: conversationId,
                user_id: userId,
                tracking_consent: true,
                is_reply: false,
                utech_technology_id: emailData.techId,
                ...(ccEmail && { cc_email: ccEmail }) // curated office emails, else common_email
            };

            const response = await apiService.sendTrackedEmail(emailPayload);
            
            if (response.success) {
                console.log('Email sent successfully:', response.data);
                // Close modals and show success message
                setPreviewModalOpen(false);
                setCurrentContactData(null);
                
                // Show success notification
                setSuccessMessage('Email sent successfully! You can track the conversation in your Inbox.');
                setTimeout(() => setSuccessMessage(''), 5000);
                
                // Trigger inbox refresh by setting a flag in localStorage
                localStorage.setItem('refreshInbox', 'true');
            } else {
                console.error('Failed to send email:', response.error);
                setErrorMessage('Failed to send email. Please try again.');
                setTimeout(() => setErrorMessage(''), 5000);
            }
        } catch (error) {
            console.error('Error sending email:', error);
            setErrorMessage('Error sending email. Please check your connection and try again.');
            setTimeout(() => setErrorMessage(''), 5000);
        }
    };

    const closeContactModal = () => {
        setContactModalOpen(false);
        setCurrentContactData(null);
    };

    const closePreviewModal = () => {
        setPreviewModalOpen(false);
        setPreviewData(null);
    };

    // Saved shows technologies only: Cosmos has no way to save a researcher
    // card any more, and the few that exist belong to internal accounts. The
    // All / Tech Cards / Researcher Cards tab strip went with it — one
    // category doesn't need a filter. Nothing is deleted; the researcher rows
    // are still in user_saved_assets if this is ever reversed.
    const filteredItems = savedItems.filter(item => item.type === SavedCardType.Tech);

    if (isLoading) {
        return (
            <div className="flex flex-col gap-6 w-full">
                <div className="text-center py-8">
                    <div className="text-gray-500">Loading saved cards...</div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col gap-6 w-full">
                <div className="text-center py-8">
                    <div className="text-red-600 mb-2">Error loading saved cards</div>
                    <div className="text-gray-500 text-sm">{error}</div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-6 w-full">

            <div className="flex flex-col sm:flex-row flex-wrap gap-4 justify-start">
                {filteredItems.length === 0 ? (
                    <div className="text-center py-8 w-full">
                        <div className="text-gray-500">No saved cards found</div>
                    </div>
                ) : (
                    filteredItems.map(item => (
                        <SavedItemCard 
                            key={item.id} 
                            item={item} 
                            isBookmarked={true} 
                            onBookmarkToggle={handleBookmarkToggle} 
                            onReadMore={handleReadMore} 
                        />
                    ))
                )}
            </div>

            {/* Modals */}
            {modalOpen && (
                <div 
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
                    onClick={() => setModalOpen(null)}
                >
                    <div 
                        className="rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-auto utech-nocopy"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {modalOpen.type === 'tech' ? (
                            <TechCard 
                            contactLocked={!upgrade.ent.can_contact}
                            onLockedContact={() => upgrade.gate(false, 'Tech Transfer Office contacts', undefined, 'can_contact')}
                                {...modalOpen.data} 
                                onClose={() => setModalOpen(null)}
                                onContact={modalOpen.data.onContact}
                                onBookmarkToggle={async (isBookmarked) => {
                                    try {
                                        const userId = apiService.getStoredUserId();
                                        const assetId = modalOpen.data.utech_technology_id || modalOpen.data.id;
                                        if (userId && assetId) {
                                            const response = await apiService.saveCard({
                                                asset_type: 'technology',
                                                asset_id: assetId,
                                                bookmarked: isBookmarked,
                                                liked: null,
                                                linked_chat_id: null
                                            }, userId);
                                            
                                            if (response.success) {
                                                // Update local state
                                                setSavedStates(prev => {
                                                    const newMap = new Map(prev);
                                                    const current = newMap.get(assetId) || {bookmarked: false, liked: null};
                                                    newMap.set(assetId, {...current, bookmarked: isBookmarked});
                                                    return newMap;
                                                });
                                                console.log('Bookmark status updated:', isBookmarked);
                                            }
                                        }
                                    } catch (error) {
                                        console.error('Error toggling bookmark:', error);
                                    }
                                }}
                                onLikeDislike={async (liked) => {
                                    try {
                                        const userId = apiService.getStoredUserId();
                                        const assetId = modalOpen.data.utech_technology_id || modalOpen.data.id;
                                        if (userId && assetId) {
                                            const response = await apiService.likeCard({
                                                asset_type: 'technology',
                                                asset_id: assetId,
                                                liked: liked,
                                                linked_chat_id: null
                                            }, userId);
                                            
                                            if (response.success) {
                                                // Update local state
                                                setSavedStates(prev => {
                                                    const newMap = new Map(prev);
                                                    const current = newMap.get(assetId) || {bookmarked: true, liked: null};
                                                    newMap.set(assetId, {...current, liked: liked});
                                                    return newMap;
                                                });
                                                console.log('Like status updated:', liked);
                                            }
                                        }
                                    } catch (error) {
                                        console.error('Error toggling like:', error);
                                    }
                                }}
                                initialBookmarked={(() => {
                                    const assetId = modalOpen.data.utech_technology_id || modalOpen.data.id;
                                    return savedStates.get(assetId)?.bookmarked ?? true;
                                })()}
                                initialLiked={(() => {
                                    const assetId = modalOpen.data.utech_technology_id || modalOpen.data.id;
                                    return savedStates.get(assetId)?.liked ?? null;
                                })()}
                            />
                        ) : (
                            <ResearcherCard 
                                {...modalOpen.data} 
                                onClose={() => setModalOpen(null)}
                                onTechCardClick={async (techId) => {
                                    setModalOpen(null);
                                    // Fetch technology details and open tech card modal
                                    try {
                                        const response = await apiService.getTechnologyDetails(techId);
                                        if (response.success && response.data) {
                                            // Extract IP and publication summaries from ip_information
                                            let ipSummary = 'No IP summary available';
                                            let publicationsSummary = 'No publications summary available';
                                            
                                            if (response.data.ip_information) {
                                                try {
                                                    const ipInfo = typeof response.data.ip_information === 'string' 
                                                        ? JSON.parse(response.data.ip_information)
                                                        : response.data.ip_information;
                                                    ipSummary = ipInfo.ip_summary || ipSummary;
                                                    publicationsSummary = ipInfo.publications_summary || publicationsSummary;
                                                } catch (e) {
                                                    console.warn('Could not parse ip_information:', e);
                                                }
                                            }
                                            
                                            // Parse researchers from response data
                                            let researchers = [];
                                            try {
                                                if (response.data.researchers && Array.isArray(response.data.researchers)) {
                                                    researchers = response.data.researchers;
                                                } else if (response.data.researcher_details) {
                                                    const researcherDetails = typeof response.data.researcher_details === 'string'
                                                        ? JSON.parse(response.data.researcher_details)
                                                        : response.data.researcher_details;
                                                    researchers = Array.isArray(researcherDetails) ? researcherDetails : [];
                                                }
                                            } catch (e) {
                                                console.warn('Could not parse researcher details:', e);
                                                researchers = [{ name: 'Unknown Researcher' }];
                                            }
                                            
                                            // Prepare tech card data
                                            const techCardData = {
                                                id: response.data.utech_technology_id || techId,
                                                utech_technology_id: response.data.utech_technology_id || techId,
                                                relevanceScore: 'Technology Card', // Show 'Technology Card' instead of percentage
                                                title: response.data.utech_title || response.data.title || techId,
                                                institution: response.data.university_name || '',
                                                location: response.data.university_country || '',
                                                trl: response.data.trl || 'U',
                                                trlReasoning: response.data.trl_reasoning,
                                                researchers: researchers,
                                                summary: response.data.utech_summary || response.data.summary || 'Technology summary not available',
                                                ipSummary: ipSummary,
                                                publicationsSummary: publicationsSummary,
                                                activeTab: "Summary" as const,
                                                onContact: () => handleContactUniversity({
                                                    techTitle: response.data.utech_title || response.data.title || techId,
                                                    techId: response.data.utech_technology_id || techId, // Always use utech_technology_id for data fetching
                                                    surface: 'saved_researcher',
                                                    university: response.data.university_name || '',
                                                    referenceNumber: response.data.reference_number || '', // Separate reference number for display
                                                    researcherName: researchers[0]?.name || 'Researcher',
                                                    userName: profileData ? `${profileData.firstName} ${profileData.lastName}` : '',
                                                    personalSummary: profileData?.summary || '',
                                                    rdSummary: rdProfileData?.rdSummary || ''
                                                }),
                                                onClose: () => setModalOpen(null)
                                            };
                                            
                                            // Open tech card modal
                                            setModalOpen({type: 'tech', data: techCardData});
                                        } else {
                                            console.error('Failed to fetch technology details:', response.error);
                                        }
                                    } catch (error) {
                                        console.error('Error fetching technology details:', error);
                                    }
                                }}
                                onBookmarkToggle={async (isBookmarked) => {
                                    try {
                                        const userId = apiService.getStoredUserId();
                                        const assetId = modalOpen.data.utech_researcher_id || modalOpen.data.id;
                                        if (userId && assetId) {
                                            const response = await apiService.saveCard({
                                                asset_type: 'researcher',
                                                asset_id: assetId,
                                                bookmarked: isBookmarked,
                                                liked: null,
                                                linked_chat_id: null
                                            }, userId);
                                            
                                            if (response.success) {
                                                // Update local state
                                                setSavedStates(prev => {
                                                    const newMap = new Map(prev);
                                                    const current = newMap.get(assetId) || {bookmarked: false, liked: null};
                                                    newMap.set(assetId, {...current, bookmarked: isBookmarked});
                                                    return newMap;
                                                });
                                                console.log('Bookmark status updated:', isBookmarked);
                                            }
                                        }
                                    } catch (error) {
                                        console.error('Error toggling bookmark:', error);
                                    }
                                }}
                                onLikeDislike={async (liked) => {
                                    try {
                                        const userId = apiService.getStoredUserId();
                                        const assetId = modalOpen.data.utech_researcher_id || modalOpen.data.id;
                                        if (userId && assetId) {
                                            const response = await apiService.likeCard({
                                                asset_type: 'researcher',
                                                asset_id: assetId,
                                                liked: liked,
                                                linked_chat_id: null
                                            }, userId);
                                            
                                            if (response.success) {
                                                // Update local state
                                                setSavedStates(prev => {
                                                    const newMap = new Map(prev);
                                                    const current = newMap.get(assetId) || {bookmarked: true, liked: null};
                                                    newMap.set(assetId, {...current, liked: liked});
                                                    return newMap;
                                                });
                                                console.log('Like status updated:', liked);
                                            }
                                        }
                                    } catch (error) {
                                        console.error('Error toggling like:', error);
                                    }
                                }}
                                initialBookmarked={(() => {
                                    const assetId = modalOpen.data.utech_researcher_id || modalOpen.data.id;
                                    return savedStates.get(assetId)?.bookmarked ?? true;
                                })()}
                                initialLiked={(() => {
                                    const assetId = modalOpen.data.utech_researcher_id || modalOpen.data.id;
                                    return savedStates.get(assetId)?.liked ?? null;
                                })()}
                            />
                        )}
                    </div>
                </div>
            )}

            {/* Contact University Modal (kept mounted so edits persist across preview) */}
            <ContactUniversityModal
                onTalkToConcierge={onTalkToConcierge}
                surface={currentContactData?.surface || 'saved_researcher'}
                university={currentContactData?.university || ''}
                isOpen={!!contactModalOpen && !!currentContactData}
                onClose={closeContactModal}
                onPreview={handlePreviewMessage}
                techTitle={currentContactData?.techTitle || ''}
                techId={currentContactData?.techId || ''}
                referenceNumber={currentContactData?.referenceNumber}
                researcherName={currentContactData?.researcherName || ''}
                userName={currentContactData?.userName || ''}
                personalSummary={currentContactData?.personalSummary || ''}
                rdSummary={currentContactData?.rdSummary || ''}
            />

            {/* Preview Message Modal */}
            <PreviewMessageModal
                isOpen={!!previewModalOpen && !!previewData && !!currentContactData}
                onClose={closePreviewModal}
                message={previewData?.message || ''}
                summary={previewData && currentContactData
                    ? (previewData.summaryType === 'personal' ? 'Professional Summary' : 'R&D Summary')
                    : ''}
                techId={currentContactData?.techId || ''}
                referenceNumber={currentContactData?.referenceNumber || ''} // Add reference number for display
                techManagerName={previewData?.techManagerName || ''}
                techTitle={currentContactData?.techTitle || ''}
                userName={currentContactData?.userName || ''}
                onEdit={() => {
                    closePreviewModal();
                    setContactModalOpen(true);
                }}
                onSend={handleSendEmail}
            />

            {/* Success/Error Notifications */}
            <FloatingToast variant="success" message={successMessage} visible={!!successMessage} />
            <FloatingToast variant="error" message={errorMessage} visible={!!errorMessage} />
        </div>
    );
};